import { useEffect, useState } from 'react';
import {
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Target,
  Tags,
  WalletCards,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { useCleviaGuide } from '@/contexts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const SLIDE_COUNT = 7;
const FINAL_STEP = SLIDE_COUNT;

export function CleviaGettingStartedModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, closeGuide } = useCleviaGuide();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen]);

  const isFinalSlide = currentStep === FINAL_STEP;
  const isWelcome = currentStep === 0;

  const goNext = () => {
    if (isFinalSlide) {
      closeGuide();
      return;
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    const featureRoutes: Record<number, string> = {
      1: '/accounts#guide-accounts',
      2: '/transactions#guide-transactions',
      3: '/budgets#guide-budgets',
      4: '/calendar#guide-calendar',
      5: '/goals#guide-goals',
      6: '/settings?tab=categories#guide-categories',
    };
    const route = featureRoutes[nextStep];
    if (route) navigate(route);
  };

  const goPrevious = () => setCurrentStep((step) => Math.max(0, step - 1));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeGuide()}>
      <DialogContent className="max-h-[min(760px,90vh)] overflow-y-auto sm:max-w-xl">
        <DialogHeader className="pr-8">
          <div className="mb-1 flex items-center gap-2 text-[#16c47f]">
            <BookOpen className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.12em]">
              {t('gettingStarted.label' as any)}
            </span>
          </div>
          <DialogTitle className="text-2xl leading-tight">
            {t(
              (isFinalSlide
                ? 'gettingStarted.final.title'
                : `gettingStarted.slide${currentStep + 1}.title`) as any
            )}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {t(
              (isFinalSlide
                ? 'gettingStarted.final.description'
                : `gettingStarted.slide${currentStep + 1}.description`) as any
            )}
          </DialogDescription>
        </DialogHeader>

        <div key={currentStep} className="animate-slide-up space-y-4 py-2">
          {currentStep === 0 && (
            <div className="rounded-xl border border-[#16c47f]/20 bg-[#16c47f]/5 p-5 text-center">
              <BookOpen className="mx-auto mb-3 h-10 w-10 text-[#16c47f]" />
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {t('gettingStarted.slide1.detail' as any)}
              </p>
            </div>
          )}

          {currentStep === 1 && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700/50 dark:bg-gray-800/60">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <WalletCards className="h-5 w-5 text-[#16c47f]" />
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {t('gettingStarted.slide2.cardTitle' as any)}
                  </span>
                </div>
                <span className="text-xs text-gray-400">
                  {t('gettingStarted.slide2.cardType' as any)}
                </span>
              </div>
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                {t('gettingStarted.slide2.balanceLabel' as any)}
              </p>
              <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
                {t('gettingStarted.slide2.amount' as any)}
              </p>
              <div className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-700">
                <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {t('gettingStarted.slide2.cardNote' as any)}
                </p>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                {t('gettingStarted.slide2.cardHelp' as any)}
              </p>
            </div>
          )}

          {currentStep === 2 && (
            <div className="grid gap-2 sm:grid-cols-3">
              <TransactionType
                icon={<ArrowDownRight />}
                title={t('gettingStarted.slide3.expenseTitle' as any)}
                text={t('gettingStarted.slide3.expenseText' as any)}
                tone="red"
              />
              <TransactionType
                icon={<ArrowUpRight />}
                title={t('gettingStarted.slide3.incomeTitle' as any)}
                text={t('gettingStarted.slide3.incomeText' as any)}
                tone="green"
              />
              <TransactionType
                icon={<ArrowLeftRight />}
                title={t('gettingStarted.slide3.transferTitle' as any)}
                text={t('gettingStarted.slide3.transferText' as any)}
                tone="blue"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 sm:col-span-3">
                {t('gettingStarted.slide3.note' as any)}
              </p>
            </div>
          )}

          {currentStep === 3 && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700/50 dark:bg-gray-800/60">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-semibold text-gray-900 dark:text-white">
                  {t('gettingStarted.slide4.exampleTitle' as any)}
                </span>
                <span className="text-sm font-semibold text-[#16c47f]">120 € / 200 €</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div className="h-full w-3/5 rounded-full bg-[#16c47f]" />
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('gettingStarted.slide4.exampleText' as any)}
              </p>
              <p className="mt-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                {t('gettingStarted.slide4.note' as any)}
              </p>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {['netflix', 'rent', 'gym', 'salary', 'insurance'].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-700/50 dark:bg-gray-800/60 dark:text-gray-300"
                  >
                    <CalendarDays className="h-4 w-4 text-[#16c47f]" />
                    {t(`gettingStarted.slide5.${item}` as any)}
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-[#16c47f]/20 bg-[#16c47f]/5 p-4">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {t('gettingStarted.slide5.cardTitle' as any)}
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {t('gettingStarted.slide5.cardText' as any)}
                </p>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="grid gap-3 sm:grid-cols-2">
              <GoalType
                icon={<Target />}
                title={t('gettingStarted.slide6.savingsTitle' as any)}
                text={t('gettingStarted.slide6.savingsText' as any)}
                examples={t('gettingStarted.slide6.savingsExamples' as any)}
              />
              <GoalType
                icon={<ArrowDownRight />}
                title={t('gettingStarted.slide6.debtTitle' as any)}
                text={t('gettingStarted.slide6.debtText' as any)}
                examples={t('gettingStarted.slide6.debtExamples' as any)}
              />
              <p className="text-xs leading-relaxed text-gray-400 dark:text-gray-500 sm:col-span-2">
                {t('gettingStarted.slide6.note' as any)}
              </p>
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700 dark:border-gray-700/50 dark:bg-gray-800/60 dark:text-gray-300">
                <Tags className="mr-2 inline h-4 w-4 text-[#16c47f]" />
                {t('gettingStarted.slide7.path' as any)}
              </div>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {t('gettingStarted.slide7.detail' as any)}
              </p>
              <ul className="grid gap-2 text-sm text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                {['create', 'edit', 'organize', 'hide'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#16c47f]" />
                    {t(`gettingStarted.slide7.${item}` as any)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isFinalSlide && (
            <div className="rounded-xl border border-[#16c47f]/20 bg-[#16c47f]/5 p-5 text-center">
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {t('gettingStarted.final.detail' as any)}
              </p>
              <p className="mt-5 text-sm font-semibold text-gray-900 dark:text-white">
                {t('gettingStarted.final.names' as any)}
                <br />
                <span className="font-normal text-gray-500 dark:text-gray-400">
                  {t('gettingStarted.final.signature' as any)}
                </span>
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4 border-t border-gray-100 pt-4 dark:border-gray-700/50">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>
              {isFinalSlide
                ? t('gettingStarted.finalStep' as any)
                : `${currentStep + 1} / ${SLIDE_COUNT}`}
            </span>
            <div className="flex gap-1">
              {!isFinalSlide &&
                Array.from({ length: SLIDE_COUNT }).map((_, index) => (
                  <span
                    key={index}
                    className={`h-1.5 rounded-full transition-all ${index === currentStep ? 'w-6 bg-[#16c47f]' : 'w-1.5 bg-gray-200 dark:bg-gray-700'}`}
                  />
                ))}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={goPrevious} disabled={isWelcome} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              {t('gettingStarted.previous' as any)}
            </Button>
            <Button onClick={goNext} className="gap-2 bg-[#16c47f] text-white hover:bg-[#12b070]">
              {isFinalSlide
                ? t('gettingStarted.final.cta' as any)
                : isWelcome
                  ? t('gettingStarted.start' as any)
                  : t('gettingStarted.next' as any)}
              {isFinalSlide ? <Check className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransactionType({
  icon,
  title,
  text,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  tone: 'red' | 'green' | 'blue';
}) {
  const toneClasses = {
    red: 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    blue: 'bg-blue-50 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400',
  };
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700/50 dark:bg-gray-800/60">
      <div
        className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${toneClasses[tone]}`}
      >
        {icon}
      </div>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{text}</p>
    </div>
  );
}

function GoalType({
  icon,
  title,
  text,
  examples,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  examples: string;
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700/50 dark:bg-gray-800/60">
      <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-[#16c47f]/10 text-[#16c47f]">
        {icon}
      </div>
      <p className="font-semibold text-gray-900 dark:text-white">{title}</p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{text}</p>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{examples}</p>
    </div>
  );
}
